import React from "react";

interface SlideContent {
  mainQuestion?: string;
  subQuestions?: string[];
  guideText?: string;
  topEmoji?: string;
  bottomEmojis?: string[];
  bgColor?: string;
  showDecorations?: boolean;
  showSubQuestions?: boolean;
  showGuideText?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const QuestionPromptSlide3 = {
  id: "question-prompt-slide3",
  name: "Question / Prompt Slide Style 3",
  fields: [
    {
      id: "mainQuestion",
      label: "Main Question",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subQuestions",
      label: "Sub Questions (Array)",
      type: "text",
      defaultValue: JSON.stringify(["", "", "", ""]),
    },
    {
      id: "guideText",
      label: "Guide Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "topEmoji",
      label: "Top Emoji",
      type: "text",
      defaultValue: "🎯",
    },
    {
      id: "bottomEmojis",
      label: "Bottom Emojis (Array)",
      type: "text",
      defaultValue: JSON.stringify(["💭", "🔍", "💡"]),
    },
    {
      id: "showDecorations",
      label: "Show Decorations",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showSubQuestions",
      label: "Show Sub Questions",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showGuideText",
      label: "Show Guide Text",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      mainQuestion = "",
      subQuestions = ["", "", "", ""],
      guideText = "",
      topEmoji = "🎯",
      bottomEmojis = ["💭", "🔍", "💡"],
      showDecorations = true,
      showSubQuestions = true,
      showGuideText = true,
      bgColor = "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
      onEdit = () => {},
    } = content;

    // Parse arrays from string if needed
    const parsedSubQuestions = Array.isArray(subQuestions)
      ? subQuestions
      : typeof subQuestions === "string"
        ? JSON.parse(subQuestions)
        : ["", "", "", ""];

    const parsedBottomEmojis = Array.isArray(bottomEmojis)
      ? bottomEmojis
      : typeof bottomEmojis === "string"
        ? JSON.parse(bottomEmojis)
        : ["💭", "🔍", "💡"];

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleSubQuestionEdit = (index: number) => (value: string) => {
      const newSubQuestions = [...parsedSubQuestions];
      newSubQuestions[index] = value;
      handleEdit("subQuestions")(JSON.stringify(newSubQuestions));
    };

    const handleBottomEmojiEdit = (index: number) => (value: string) => {
      const newEmojis = [...parsedBottomEmojis];
      newEmojis[index] = value;
      handleEdit("bottomEmojis")(JSON.stringify(newEmojis));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Decorations */}
        {showDecorations && (
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-64 h-64 bg-teal-400/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-teal-400/10 rounded-full translate-x-1/2 translate-y-1/2" />
          </div>
        )}

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          {/* Top Emoji */}
          <div className="mb-8">
            <EditableDiv
              text={topEmoji}
              onEdit={handleEdit("topEmoji")}
              className="text-6xl transform hover:scale-110 transition-transform inline-block"
            />
          </div>

          {/* Main Question */}
          <div className="text-center mb-12 max-w-3xl">
            <EditableDiv
              text={mainQuestion}
              onEdit={handleEdit("mainQuestion")}
              className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-teal-200 to-teal-100"
            />
          </div>

          {/* Sub Questions */}
          {showSubQuestions && (
            <div className="grid grid-cols-2 gap-6 max-w-4xl w-full mb-12">
              {parsedSubQuestions.map((question, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-l-4 border-teal-400/30"
                >
                  <EditableDiv
                    text={question}
                    onEdit={handleSubQuestionEdit(idx)}
                    className="text-lg text-teal-100/90"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Guide Text */}
          {showGuideText && (
            <div className="max-w-2xl text-center mb-8">
              <EditableDiv
                text={guideText}
                onEdit={handleEdit("guideText")}
                className="text-xl text-teal-100/80 italic"
              />
            </div>
          )}

          {/* Bottom Emojis */}
          <div className="flex justify-center items-center gap-8">
            {parsedBottomEmojis.map((emoji, idx) => (
              <EditableDiv
                key={idx}
                text={emoji}
                onEdit={handleBottomEmojiEdit(idx)}
                className="text-3xl transform hover:scale-110 transition-transform"
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default QuestionPromptSlide3;
